<div class="trust-analysis-block" [ngClass]="{ superseded: superseded }">
  <button type="button" class="trust-header" (click)="toggle()">
    <span class="mdi trust-chevron" [ngClass]="expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span>
    <span class="trust-title-group">
      <span class="trust-title">
        Trust Deed Analysis
        @if (superseded) {
        <span class="trust-superseded-tag">Superseded</span>
        }
      </span>
      <span class="trust-subtitle">{{ subtitle }}</span>
      @if (identity) {
      <span class="trust-subtitle">{{ identity }}</span>
      }
    </span>
    @if (problem) {
    <span class="mdi mdi-alert-circle-outline trust-attention"
      matTooltip="This trust analysis needs attention"></span>
    }
    <span class="trust-status" [ngClass]="displayStatus">{{ statusLabel }}</span>
  </button>

  @if (expanded) {
  <div class="trust-detail">
    @if (problem) {
    <message-box [type]="failed ? 'danger' : 'warn'">
      <div>{{ problem }}</div>
    </message-box>
    }

    <div class="deed-row">
      <div class="deed-file">
        <span class="mdi mdi-file-pdf-box deed-icon"></span>
        <span class="deed-name" [matTooltip]="analysis.fileName">{{ analysis.fileName }}</span>
        <span class="deed-meta">
          {{ fileSizeLabel(analysis.fileSizeBytes) }}
          · Uploaded {{ analysis.createdAt | date:'dd/MM/yyyy h:mm a' }}
        </span>
      </div>
      <div class="deed-actions">
        <button mat-stroked-button
          [disabled]="!analysis.blobPath"
          [matTooltip]="analysis.blobPath ? 'Download the uploaded trust deed' : 'The deed was not stored'"
          (click)="downloadDeed.emit(analysis)">Download Deed</button>
        @if (versions.length > 1) {
        <button mat-stroked-button [matMenuTriggerFor]="versionsMenu"
          matTooltip="Switch between the deeds uploaded against this condition">
          <span class="mdi mdi-history"></span> Versions ({{ versions.length }})
        </button>
        <mat-menu #versionsMenu="matMenu">
          @for (version of versions; track version.runId) {
          <button mat-menu-item class="version-item" (click)="selectVersion.emit(version)">
            <span class="mdi version-tick"
              [ngClass]="isCurrentVersion(version) ? 'mdi-check' : ''"></span>
            {{ version.fileName }}
            <span class="version-meta">{{ versionMeta(version) }}</span>
          </button>
          }
        </mat-menu>
        }
        @if (failed) {
        <button mat-stroked-button
          matTooltip="Re-run the analysis using the deed already uploaded"
          (click)="retry.emit(analysis)">Retry Analysis</button>
        }
      </div>
    </div>

    @if (pending) {
    <message-box type="info">
      <p>FrankieOne is reading the deed. This usually takes a couple of minutes; the extracted
        structure appears here once it finishes.</p>
    </message-box>
    }

    @if (analysis.result) {
    <div class="details-content">
      <div class="section-title">Trust Details</div>
      <div class="fact-grid">
        <div class="fact">
          <span class="fact-label">Trust name</span>
          <span class="fact-value">{{ displayValue(analysis.result.trustName) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Trust type</span>
          <span class="fact-value">{{ trustTypeLabel(analysis.result.detectedTrustType) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Established</span>
          <span class="fact-value">
            @if (analysis.result.establishmentDate) {
            {{ analysis.result.establishmentDate | date:'dd/MM/yyyy' }}
            } @else {
            <span class="not-available">—</span>
            }
          </span>
        </div>
        <div class="fact">
          <span class="fact-label">Jurisdiction</span>
          <span class="fact-value">{{ displayValue(analysis.result.establishmentSubdivision ?? analysis.result.establishmentCountry) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Executed</span>
          <span class="fact-value">
            @if (analysis.result.executionDate) {
            {{ analysis.result.executionDate | date:'dd/MM/yyyy' }}
            } @else {
            <span class="not-available">—</span>
            }
          </span>
        </div>
        <div class="fact">
          <span class="fact-label">Certified</span>
          <span class="fact-value">
            @if (analysis.result.certificationDate) {
            {{ analysis.result.certificationDate | date:'dd/MM/yyyy' }}
            } @else {
            <span class="not-available">—</span>
            }
          </span>
        </div>
        @if (analysis.result.totalUnitsOnIssue !== null) {
        <div class="fact">
          <span class="fact-label">Units on issue</span>
          <span class="fact-value">{{ analysis.result.totalUnitsOnIssue }}</span>
        </div>
        }
        <div class="fact">
          <span class="fact-label">Reviewer confirmed</span>
          <span class="fact-value">{{ analysis.result.isConfirmed ? 'Yes' : 'Not yet' }}</span>
        </div>
      </div>
    </div>

    @for (group of partyGroups; track group.role) {
    <div class="details-content">
      <div class="section-subtitle">{{ group.label }} ({{ group.parties.length }})</div>
      <table mat-table [dataSource]="group.parties" class="parties-table">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Name</th>
          <td mat-cell *matCellDef="let party">{{ partyName(party) }}</td>
        </ng-container>
        <ng-container matColumnDef="kind">
          <th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let party">{{ partyKind(party) }}</td>
        </ng-container>
        <ng-container matColumnDef="detail">
          <th mat-header-cell *matHeaderCellDef>Details</th>
          <td mat-cell *matCellDef="let party">{{ partyDetail(party) }}</td>
        </ng-container>
        <ng-container matColumnDef="pages">
          <th mat-header-cell *matHeaderCellDef>Deed page</th>
          <td mat-cell *matCellDef="let party">{{ partyPages(party) }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="partyColumns"></tr>
        <tr mat-row *matRowDef="let party; columns: partyColumns"></tr>
      </table>
    </div>
    }

    @if (!partyGroups.length && !failed) {
    <p class="register-note">No parties were extracted from this deed.</p>
    }
    }
  </div>
  }
</div>
